/**
 * Phase 9 (Phase 20: the country's technical halt only — a stop-loss halts
 * its own campaign group, see GroupHaltBanner) — the red banner while a send
 * needs a person: it names the failure and stops every group. When a send is
 * stuck the person answers what SMSWiser shows — It went out / It did not go
 * out — and that answer resumes. Resume sits on the kill permission.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { CheckCircle2, Play, Wrench, XCircle } from 'lucide-react';

import { Button } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import { dkThenLocal } from './autosend-fact';
import { type StuckOutcome, useAutoSendMutations } from './use-autosend-mutations';

/** The server's reason as a sentence — it may not end with a full stop. */
const sentence = (reason: string | null) => (reason && !/[.!?]$/.test(reason) ? `${reason}.` : reason);

export const AutoSendHaltBanner: FC<{ plan: DropGenPlanDetail; stuckAtUtc: string[] }> = ({ plan, stuckAtUtc }) => {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const { resume } = useAutoSendMutations(plan.countryKey);
  if (plan.autoSendHaltKind !== 'TECHNICAL') return null;
  const stuck = stuckAtUtc.length > 0;
  const run = async (outcome?: StuckOutcome) => {
    try {
      await resume.mutateAsync(outcome);
      success('Auto-send resumed — the next due draft goes on the next minute');
    } catch (err) {
      error('Auto-send stays halted', getApiErrorMessage(err));
    }
  };
  const times = stuckAtUtc.map((t) => dkThenLocal(t, plan.recipientTimezone)).join(', ');
  return (
    <div role="alert" className="flex items-start gap-3 rounded-xl border border-destructive/40 bg-destructive/10 p-3.5 animate-in fade-in-0 slide-in-from-top-1">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-destructive/15 text-destructive"><Wrench className="h-4 w-4" /></span>
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-semibold">Auto-send halted in every campaign group — a send needs a person</p>
        <p className="mt-0.5 text-muted-foreground">{sentence(plan.autoSendHaltReason)}</p>
        {!hasPermission('drop_generator.kill') ? <p className="mt-2 text-xs text-muted-foreground">Resuming needs the kill permission.</p> : (
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {stuck ? (
              <>
                <span className="text-xs text-muted-foreground">The drop at {times} — what does SMSWiser show?</span>
                <Button size="sm" variant="outline" loading={resume.isPending} onClick={() => void run('sent')}><CheckCircle2 className="mr-1 h-3.5 w-3.5 text-success" />It went out</Button>
                <Button size="sm" variant="outline" loading={resume.isPending} onClick={() => void run('not_sent')}><XCircle className="mr-1 h-3.5 w-3.5 text-destructive" />It did not go out</Button>
              </>
            ) : (
              <Button size="sm" variant="outline" loading={resume.isPending} onClick={() => void run()}><Play className="mr-1 h-3.5 w-3.5" />Resume</Button>
            )}
          </div>
        )}
        {stuck ? <p className="mt-2 text-xs text-muted-foreground">It went out — it counts as sent today, toward its group’s cap. It did not — the draft goes back to you; auto-send never fires it on its own.</p> : null}
      </div>
    </div>
  );
};
